/**
 * hooks/useUploadQueue.ts
 * ────────────────────────
 * Concurrent, retry-aware upload queue for the Pilot Field Interface.
 *
 * Design constraints
 * ────────────────────────────
 * 1. Concurrency: UPLOAD_CONCURRENCY (default 3) — multiple files upload in
 *    parallel so the Celery scan pipeline is fed continuously.
 *    Safe for Cloudflare because each individual upload stays well under the
 *    100-second proxy timeout, and NVMe-backed Fly volumes handle concurrent
 *    writes without contention.
 * 2. Race-free claiming — an inFlightRef Set tracks claimed item IDs
 *    synchronously before any await, preventing two workers from picking the
 *    same file. (JS is single-threaded; the sync claim is uninterruptible.)
 * 3. No URL.createObjectURL — only filename + size are stored; no DOM blobs
 *    are created so the browser tab memory stays flat across 2,000-file batches.
 * 4. Exponential backoff retry — up to MAX_RETRIES attempts per file;
 *    4xx errors (except 429) are not retried (they are deterministic failures).
 *
 * Mission IDs
 * ───────────
 * A new UUID mission_id is minted for each fresh drop batch.  If files are
 * added while a batch is already in progress, they join the same mission.
 * Once the queue drains completely, the next drop starts a new mission.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadPhoto } from '@/api/upload';
import { ApiError } from '@/api/client';

// ── Constants ─────────────────────────────────────────────────────────────────

const MAX_RETRIES = 3;
// 6 matches the browser's own per-origin HTTP/1.1 connection ceiling
// (Chrome/Firefox cap ~6) — raising this further just queues in the
// browser's network stack rather than adding real parallelism, and on a
// bandwidth-saturated/lossy link (weak warehouse WiFi) more streams can add
// contention without more throughput. Bumped from 4 after full-res uploads
// (~5-7MB/photo) made upload bandwidth the bottleneck, not server decode.
const UPLOAD_CONCURRENCY = 6;

// ── Client-side resize cap (default 1920px; PER-WAREHOUSE override) ──────────
// 2026-07-15: the cap is now per-warehouse (warehouses.upload_max_width via
// the active mission's upload_max_width). The 14 Jul Reckitt stocktake proved
// 1920 destroys decode of small/dense composite pallet-QR labels (June
// full-res decoded fine; July @1920 decoded ~none) — the benchmark below
// measured Fonterra LP-18 SSCC labels, which ARE fine at 1920. Default stays
// 1920 for warehouses without an override.
// PAIRED same-photo test (120 photos, both resolutions, zxing union decoder):
//   >=1 slot tag   97.5% full-res vs 95.8% @1920
//   >=2 tags (S-Rule fuel)  88.3% vs 89.2%   (1920 marginally BETTER)
//   >=1 pallet LP-18        97.5% vs 97.5%   (identical)
// With the zxing union co-decoder in the ingest loop, resolution no longer
// moves any signal the assignment logic uses — the earlier "full-res uplift"
// was measuring the DECODER gap (Scandit-only), not resolution. Full-res cost
// ~7x upload time (75 min vs ~11 min per ~400 photos on warehouse WiFi), ~7x
// volume storage, and caused the large-QR tile-split regression that needed
// the dual-scale rescue pass. Its only measured edge — +9pp on Scandit beam
// Code128, a secondary signal — plus a FUTURE OCR layer, doesn't pay for that.
// If OCR ships for beam text, re-run the paired test WITH OCR before raising
// this cap again (decision stays evidence-driven).
const UPLOAD_MAX_WIDTH = (() => {
  const fromEnv = Number(import.meta.env.VITE_UPLOAD_MAX_WIDTH);
  return Number.isFinite(fromEnv) && fromEnv > 0 ? fromEnv : 1920;
})();

async function resizeForUpload(file: File, maxWidth: number = UPLOAD_MAX_WIDTH): Promise<File> {
  // Non-images pass through untouched.
  if (!file.type.startsWith('image/')) return file;
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.naturalWidth <= maxWidth) {
        resolve(file);  // already fits — skip canvas round-trip
        return;
      }
      const scale  = maxWidth / img.naturalWidth;
      const canvas = document.createElement('canvas');
      canvas.width  = maxWidth;
      canvas.height = Math.round(img.naturalHeight * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(file); return; }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return; }
          // Keep original filename so server logs stay meaningful.
          resolve(new File([blob], file.name, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        0.92,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

/** Delay (ms) before each retry attempt (indexed by attempt number, 1-based). */
const RETRY_DELAY_MS = [1_000, 2_000, 4_000] as const;

const ACCEPTED_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]);

// ── Types ─────────────────────────────────────────────────────────────────────

export type ItemStatus = 'queued' | 'uploading' | 'done' | 'failed';

export interface QueueItem {
  /** Stable client-side identifier (React key). */
  id: string;
  filename: string;
  /** Bytes — stored instead of a File/ObjectURL reference to avoid RAM bloat. */
  sizeBytes: number;
  status: ItemStatus;
  /** Total upload attempts made (0 = not started). */
  attempts: number;
  error?: string;
}

export interface UseUploadQueueReturn {
  items: QueueItem[];
  /** Count of successfully uploaded files in the current batch. */
  doneCount: number;
  /** Count of permanently failed files (exhausted retries or 4xx). */
  failedCount: number;
  /** True while at least one file is being uploaded. */
  isRunning: boolean;
  /** The mission UUID for the current / most-recent batch. */
  missionId: string;
  /**
   * Add files to the queue.
   * @param aisle  Aisle the pilot declared they are flying (e.g. "A").
   *               Attached to each upload so the backend can triage by aisle.
   */
  addFiles: (files: File[], aisle?: string) => void;
  /** Clear all items and mint a fresh mission UUID. */
  reset: () => void;
  /** Re-queue all permanently-failed items for another upload attempt. */
  retryFailed: () => void;
}

// ── Helper — async sleep ──────────────────────────────────────────────────────

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useUploadQueue(
  sessionId: string,
  warehouseId: string,
  /** When provided, all uploads use this mission ID instead of auto-generating one. */
  externalMissionId?: string,
  /** Per-warehouse resize cap (px, longest side) from the active mission
   *  (warehouses.upload_max_width). null/undefined → the 1920 default. */
  uploadMaxWidth?: number | null,
): UseUploadQueueReturn {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  /**
   * Mirror of `items` that's readable inside async callbacks without stale
   * closure issues.  Updated synchronously whenever `setItems` fires via
   * `updateItems`.
   */
  const itemsRef = useRef<QueueItem[]>([]);

  /** True while the processing loop is running. Prevents double-invocation. */
  const processingRef = useRef(false);
  /** IDs currently being uploaded — claimed synchronously to prevent two workers racing for the same file. */
  const inFlightRef = useRef(new Set<string>());

  /**
   * Stable mission UUID for the current batch.
   * When externalMissionId is provided (pilot selected a manager-created mission),
   * we always use that ID and never auto-rotate it.
   */
  const missionIdRef = useRef<string>(externalMissionId ?? crypto.randomUUID());

  // Ref so addFiles/reset always read the latest externalMissionId without
  // needing it in their dependency arrays (avoids stale-closure overwrites).
  const externalMissionIdRef = useRef(externalMissionId);
  externalMissionIdRef.current = externalMissionId;

  // Keep missionIdRef in sync when the external mission changes.
  useEffect(() => {
    if (externalMissionId) {
      missionIdRef.current = externalMissionId;
    }
  }, [externalMissionId]);

  // ── Synchronised state setter ─────────────────────────────────────────────

  const updateItems = useCallback(
    (updater: (prev: QueueItem[]) => QueueItem[]) => {
      setItems((prev) => {
        const next = updater(prev);
        itemsRef.current = next;
        return next;
      });
    },
    [],
  );

  // ── Concurrent processing loop ────────────────────────────────────────────
  // UPLOAD_CONCURRENCY independent workers run in parallel.  Each worker
  // claims the next queued item synchronously (via inFlightRef) before any
  // await, so two workers can never pick the same file.

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    inFlightRef.current.clear();
    setIsRunning(true);

    // Single worker coroutine — runs until no more queued items remain.
    const worker = async () => {
      while (true) {
        // ── Claim next queued item (synchronous — uninterruptible in JS) ──
        const next = itemsRef.current.find(
          (i) => i.status === 'queued' && !inFlightRef.current.has(i.id),
        );
        if (!next) break;
        inFlightRef.current.add(next.id);

        updateItems((prev) =>
          prev.map((i) =>
            i.id === next.id ? { ...i, status: 'uploading', attempts: i.attempts + 1 } : i,
          ),
        );

        let succeeded = false;
        let lastError = '';

        const file = fileMapRef.current.get(next.id);

        if (!file) {
          lastError = 'File reference lost (internal error)';
        } else {
          for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
            if (attempt > 0) await sleep(RETRY_DELAY_MS[attempt - 1]);
            try {
              const itemAisle    = aisleMapRef.current.get(next.id);
              const uploadFile   = await resizeForUpload(file, uploadMaxWidth ?? UPLOAD_MAX_WIDTH);
              await uploadPhoto(uploadFile, sessionId, missionIdRef.current, warehouseId, itemAisle);
              succeeded = true;
              break;
            } catch (err) {
              lastError = err instanceof ApiError ? err.message : 'Network error — will retry';
              if (
                err instanceof ApiError &&
                err.status >= 400 &&
                err.status < 500 &&
                err.status !== 429
              ) break;
            }
          }
        }

        inFlightRef.current.delete(next.id);
        updateItems((prev) =>
          prev.map((i) =>
            i.id === next.id
              ? { ...i, status: succeeded ? 'done' : 'failed', error: succeeded ? undefined : lastError }
              : i,
          ),
        );
      }
    };

    // Run UPLOAD_CONCURRENCY workers simultaneously.
    await Promise.all(Array.from({ length: UPLOAD_CONCURRENCY }, worker));

    processingRef.current = false;
    setIsRunning(false);
  }, [sessionId, warehouseId, updateItems]);

  // ── Re-trigger loop when new items arrive ─────────────────────────────────

  useEffect(() => {
    const hasQueued = items.some((i) => i.status === 'queued');
    if (hasQueued && !processingRef.current) {
      processQueue();
    }
  }, [items, processQueue]);

  // ── File map — keeps the actual File objects separate from React state ────
  //    (React state holds only serialisable metadata; Files live here)

  const fileMapRef  = useRef<Map<string, File>>(new Map());
  /**
   * Parallel map of item-id → aisle string.
   * Stored outside React state for the same reason as fileMapRef: no blobs,
   * no re-renders on every map mutation.
   */
  const aisleMapRef = useRef<Map<string, string | undefined>>(new Map());

  // ── addFiles ──────────────────────────────────────────────────────────────

  const addFiles = useCallback(
    (files: File[], aisle?: string) => {
      const accepted = files.filter((f) => ACCEPTED_TYPES.has(f.type.toLowerCase()));
      if (accepted.length === 0) return;

      updateItems((prev) => {
        // Use the ref so we always get the current externalMissionId even if
        // addFiles was created before activeMission was set (stale closure fix).
        const currentExternalId = externalMissionIdRef.current;
        // Mint a fresh mission UUID when the previous batch has fully settled,
        // but only when we are NOT using a manager-assigned mission ID.
        // Only mint a new UUID when there is truly no external mission.
        // If one is provided, always use it so uploads never create orphan missions.
        if (!currentExternalId) {
          const hasActive = prev.some(
            (i) => i.status === 'queued' || i.status === 'uploading',
          );
          if (!hasActive && prev.length > 0) {
            missionIdRef.current = crypto.randomUUID();
          } else if (prev.length === 0) {
            missionIdRef.current = crypto.randomUUID();
          }
        } else {
          missionIdRef.current = currentExternalId;
        }

        const newItems: QueueItem[] = accepted.map((f) => {
          const id = crypto.randomUUID();
          fileMapRef.current.set(id, f);
          aisleMapRef.current.set(id, aisle);
          return {
            id,
            filename: f.name,
            sizeBytes: f.size,
            status: 'queued',
            attempts: 0,
          };
        });

        return [...prev, ...newItems];
      });
    },
    [updateItems],
  );

  // ── reset ─────────────────────────────────────────────────────────────────

  const reset = useCallback(() => {
    fileMapRef.current.clear();
    aisleMapRef.current.clear();
    missionIdRef.current = externalMissionIdRef.current ?? crypto.randomUUID();
    updateItems(() => []);
  }, [updateItems]);

  // ── retryFailed ───────────────────────────────────────────────────────────

  const retryFailed = useCallback(() => {
    updateItems(prev =>
      prev.map(i =>
        i.status === 'failed'
          ? { ...i, status: 'queued' as const, attempts: 0, error: undefined }
          : i,
      ),
    );
  }, [updateItems]);

  // ── Derived counts ────────────────────────────────────────────────────────

  const doneCount   = items.filter((i) => i.status === 'done').length;
  const failedCount = items.filter((i) => i.status === 'failed').length;

  return {
    items,
    doneCount,
    failedCount,
    isRunning,
    missionId: missionIdRef.current,
    addFiles,
    reset,
    retryFailed,
  };
}
